import { BaseModel } from "@/models/base.model"
import { type IProductionStatement } from "@/interfaces/api/production-statement.interface"
import { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"
import { type Moment } from "moment"
import { Sale } from "@/models/sale.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import { RentalDispenseMaterialization } from "@/models/rental-dispense-materialization.model"
import type { DraftProductionStatement } from "@/models/draft-production-statement.model"
import { FileBucket } from "@/models/file-bucket.model"
import { ProductionStatementCustomerPurchaseOrder } from "@/models/production-statement-customer-purchase-order.model"
import { toCurrencyCents } from "@/components/customer_purchase_order/customer-purchase-order-matching"

export class ProductionStatement extends BaseModel implements IProductionStatement {
  public static className = "ProductionStatement" // Used for socket communication.
  public static verboseName = "productieverantwoording"
  public static override managedResolveFields = ["project"]

  project: Project
  fileBucket?: FileBucket
  sales: Sale[]
  rentalDispenseMaterializations: RentalDispenseMaterialization[]
  rentalDispenses: RentalDispense[]
  productionStatementCustomerPurchaseOrders: ProductionStatementCustomerPurchaseOrder[]

  periodStart?: Moment
  periodEnd?: Moment
  reference?: string
  disputedAt?: Moment | null
  comment?: string
  lastSentAt?: Moment | null
  lastRemindedAt?: Moment | null

  // Annotated fields
  salesSum: string
  rentalDispenseMaterializationsSum: string
  matchedAmountSum?: string
  approvalOutstanding?: boolean
  saleIds: string[]
  rentalDispenseIds: string[]
  rentalDispenseMaterializationIds: string[]

  constructor(productionStatement: IProductionStatement, managed = false) {
    super(productionStatement)

    this.project = ModelFactory.childModel(Project, productionStatement.project, managed)
    this.fileBucket = productionStatement.fileBucket
      ? ModelFactory.childModel(FileBucket, productionStatement.fileBucket, managed)
      : undefined
    this.sales = ModelFactory.childModels(Sale, productionStatement.sales, managed)
    this.rentalDispenseMaterializations = ModelFactory.childModels(
      RentalDispenseMaterialization,
      productionStatement.rentalDispenseMaterializations,
      managed
    )
    this.rentalDispenses = ModelFactory.childModels(
      RentalDispense,
      productionStatement.rentalDispenses,
      managed
    )
    this.productionStatementCustomerPurchaseOrders = ModelFactory.childModels(
      ProductionStatementCustomerPurchaseOrder,
      productionStatement.productionStatementCustomerPurchaseOrders ?? [],
      managed
    )

    this.periodStart = productionStatement.periodStart
    this.periodEnd = productionStatement.periodEnd
    this.reference = productionStatement.reference
    this.disputedAt = productionStatement.disputedAt
    this.comment = productionStatement.comment
    this.lastSentAt = productionStatement.lastSentAt
    this.lastRemindedAt = productionStatement.lastRemindedAt

    this.salesSum = productionStatement.salesSum ?? "0"
    this.rentalDispenseMaterializationsSum =
      productionStatement.rentalDispenseMaterializationsSum ?? "0"
    this.matchedAmountSum = productionStatement.matchedAmountSum
    this.approvalOutstanding = productionStatement.approvalOutstanding
    this.saleIds = productionStatement.saleIds ?? []
    this.rentalDispenseIds = productionStatement.rentalDispenseIds ?? []
    this.rentalDispenseMaterializationIds =
      productionStatement.rentalDispenseMaterializationIds ?? []
  }

  /** The total sum of sales and rental dispense materializations of the production statement. */
  public get sum() {
    return parseFloat(this.salesSum) + parseFloat(this.rentalDispenseMaterializationsSum)
  }

  public getMatchedQuantity(): number {
    // Refreshed annotations also take precedence over an already-open detail's relations.
    return (
      toCurrencyCents(
        this.matchedAmountSum ??
          this.productionStatementCustomerPurchaseOrders.reduce(
            (sum, allocation) => sum + Number(allocation.amount),
            0
          )
      ) / 100
    )
  }

  public getOutstandingQuantity(): number {
    return (toCurrencyCents(this.getQuantity()) - toCurrencyCents(this.getMatchedQuantity())) / 100
  }

  public isOutstanding(): boolean {
    return this.approvalOutstanding ?? (!this.hasApproval() || this.getOutstandingQuantity() !== 0)
  }

  public hasApproval(): boolean {
    return (
      !this.isDraft() &&
      (this.getMatchedQuantity() !== 0 ||
        (this.getQuantity() === 0 &&
          (this.approvalOutstanding !== undefined
            ? !this.approvalOutstanding
            : this.productionStatementCustomerPurchaseOrders.length > 0)))
    )
  }

  public getQuantity(): number {
    return toCurrencyCents(this.sum) / 100
  }

  public getDisplayText(): string {
    return this.reference ? `productieverantwoording ${this.reference}` : "productieverantwoording"
  }

  public isDraft(): this is DraftProductionStatement {
    return false
  }

  /** The ISO week numbers of the production statement. */
  public get displayPeriodISOWeek(): string {
    const startWeek = this.periodStart?.isoWeek()
    const endWeek = this.periodEnd?.isoWeek()

    if (startWeek === undefined) return endWeek === undefined ? "" : `wk ${endWeek}`
    if (endWeek === undefined || endWeek === startWeek) return `wk ${startWeek}`

    return `wk ${startWeek} t/m wk ${endWeek}`
  }
}
